import { useState, useRef, useEffect, useMemo } from "react";
import { Toast } from "primereact/toast";
import Editor from "../components/UI/editor";
import MyButton from "../components/UI/Button";
import { t } from "i18next";
import {
  useGetOrderSortQuery,
  useOrderPaymentMutation,
  useOrderSortMutation,
} from "../store/api/orderSortApi";
import { Sidebar } from "primereact/sidebar";
import { SpeedDial } from "primereact/speeddial";
import PresortDemo from "../components/table/DataTable";
import { useTranslatedColumns } from "../hooks/useTranslatedColumns";
import { orderColumnSort } from "../metadata/order";
import {
  useCreateOrUpdateOrderMutation,
  useUpdatetUserOrderMutation,
} from "../store/api/orderAPI";
import { useGetUsersQuery } from "../store/api/userAPI";
import { useActionsHook } from "../hooks/useActionsHook";
import ExcelImportModal from "../components/ExcelImportModal";
import { calcShippingCost } from "../helpers/shipping";

export default function OrderScanPage() {
  const { data, isLoading, refetch } = useGetOrderSortQuery();
  const [orderPay] = useOrderPaymentMutation();
  const [setScanned] = useOrderSortMutation();
  const toast = useRef(null);
  const scanEditor = useRef(null);

  const [formData, setFormData] = useState({});
  const [formOrderData, setFormOrderData] = useState({});
  const [newUser, setNewUser] = useState({});
  const [excelOpen, setExcelOpen] = useState(false);

  const { data: newUserData } = useGetUsersQuery(
    { search: newUser?.new_user },
    { skip: !newUser?.new_user }
  );

  const [createOrUpdateOrder] = useCreateOrUpdateOrderMutation();
  const [updateUser] = useUpdatetUserOrderMutation();

  // ------------ Actions Toolbar ------------
  const actions = useActionsHook([
    {
      onClick: (param, row) => toggleNewUser(true, row),
      icon: "pi pi-user",
    },
    {
      onClick: (param, row) => togglePayment(true, row),
      icon: "pi pi-dollar",
    },
  ]);

  // ------------ Scan input ------------
  const onBlur = (e, attr, value) => {
    setFormData((state) => ({ ...state, [attr]: value }));
  };

  useEffect(() => {
    async function handleScan() {
      if (!formData?.tcode) return;

      const result = await setScanned({ tracking_number: formData.tcode });
      if (result.data.result.address_delivery) {
        toast.current.show({
          severity: "info",
          summary: "Есть доставка на заказ",
          detail: result.data.result.address_delivery,
          life: 6000,
        });
      } else {
        toast.current.show({
          severity: "success",
          summary: t("Отсканировано"),
          life: 3000,
        });
      }

      setFormData({ tcode: "" });
      scanEditor.current?.focus?.();
    }

    handleScan();
  }, [formData?.tcode]);

  // ------------ Excel import ------------
  const handleExcelRow = async (row) => {
    // 1. принять заказ (как при скане)
    const res = await setScanned({ tracking_number: row.tracking_number });
    const order = res?.data?.result;
    if (!order) throw new Error(t("Заказ не найден в системе"));

    // 2. обновить параметры и пересчитать стоимость доставки
    const updated = {
      ...order,
      gross_weight: row.gross_weight,
      width: row.width,
      height: row.height,
      length: row.length,
    };

    const { data } = await createOrUpdateOrder({
      ...updated,
      shipping_cost: calcShippingCost(updated) || 0,
    });

    if (!data?.success) throw new Error(t("Не удалось обновить параметры"));
  };

  const handleExcelDone = ({ total, failed }) => {
    toast.current.show({
      severity: failed.length ? "warn" : "success",
      summary: `${t("Обработано")}: ${total - failed.length} / ${total}`,
      life: 5000,
    });
    refetch();
  };

  // ------------ Sidebars ------------

  const [visibleLeft, setVisibleLeft] = useState({
    visible: false,
    data: null,
  });

  const [userModal, setUserModal] = useState({
    visible: false,
    data: null,
  });

  const [paymentModal, setPaymentModal] = useState({
    visible: false,
    data: null,
  });

  function toggleSidebar(bool, data) {
    setFormOrderData(data);
    setVisibleLeft({ visible: bool, data });
  }

  function toggleNewUser(bool, data) {
    setUserModal({ visible: bool, data });
    setNewUser({});
  }

  function togglePayment(bool, data) {
    if (!data) {
      setPaymentModal({
        visible: false,
        data: {},
      });
      return;
    }
    let rate = 0;
    if (data?.country_guid === "9B785514-12A9-4336-A927-C36A46596D3D") {
      rate = 999;
    } else if (data?.country_guid === "A4D9A734-CCEC-49AF-A5B5-6D5765639D21") {
      rate = 1100;
    }
    const weight = Math.ceil((data.gross_weight ?? 0) * 10) / 10;

    let summa =
      data?.shipping_cost && data?.shipping_cost > 0
        ? Number(data.shipping_cost)
        : weight * rate;
    setPaymentModal({
      visible: bool,
      data: { ...data, summa: summa },
    });
  }

  const onOrderBlur = (e, attr, value) => {
    setFormOrderData((state) => ({ ...state, [attr]: value }));
  };

  const orderColumnTr = useTranslatedColumns(orderColumnSort);

  const calculatedValue = useMemo(
    () => calcShippingCost(formOrderData),
    [formOrderData]
  );

  return (
    <div style={{ padding: "16px 0" }}>
      <Toast ref={toast} />

      {/* -------- Scan input -------- */}
      <div
        style={{
          display: "flex",
          gap: 8,
          marginBottom: 12,
          paddingLeft: 12,
          alignItems: "center",
        }}
      >
        <div style={{ width: 360, display: "flex", gap: 8 }}>
          <Editor
            onBlur={onBlur}
            data={formData}
            attr="tcode"
            trim
            refEl={scanEditor}
            placeholder={t("Сканируйте трек-код !")}
          />

          {formData?.tcode && (
            <MyButton
              label={t("Очистить")}
              outlined
              onClick={() => setFormData({ tcode: "" })}
            />
          )}
        </div>

        <MyButton
          label={t("Добавить с таблицы Excel")}
          icon="pi pi-file-excel"
          outlined
          onClick={() => setExcelOpen(true)}
        />
      </div>

      {/* -------- Table -------- */}
      <PresortDemo
        isLoading={isLoading}
        data={data?.result ?? []}
        height="calc(100vh - 130px)"
        column={orderColumnTr.filter((i) => !i.hidden)}
        toggleSidebar={toggleSidebar}
        actions={actions}
      />

      {/* -------- Floating Add Button -------- */}
      <SpeedDial
        model={[]}
        radius={120}
        type="quarter-circle"
        onClick={() => {
          toggleSidebar(true, { status: 24 });
        }}
        direction="up-left"
        style={{ right: 15, bottom: 15 }}
      />

      {/* -------- Modal: Excel import -------- */}
      <ExcelImportModal
        visible={excelOpen}
        onHide={() => setExcelOpen(false)}
        onRow={handleExcelRow}
        onDone={handleExcelDone}
      />

      {/* -------- Sidebar: Edit Order -------- */}
      <Sidebar
        visible={visibleLeft.visible}
        position="right"
        onHide={() => toggleSidebar(false)}
        style={{ width: "50vw" }}
      >
        <div className="user-sidebar">
          <div className="user-sidebar-edit">
            <p>
              Стоимость доставки с учетом объемного веса:{" "}
              {calculatedValue > 0 ? calculatedValue : 0}
            </p>
            <br />
            {orderColumnTr
              .filter((i) => i.editor)
              .map((el) => (
                <div key={el.field}>
                  <Editor {...el} onBlur={onOrderBlur} data={formOrderData} />
                </div>
              ))}

            <MyButton
              label={t("save")}
              onClick={async () => {
                if (
                  !formOrderData.country_guid ||
                  !formOrderData.tracking_number
                ) {
                  toast.current.show({
                    severity: "error",
                    summary: t("НЕ удалось сохранить заказ!"),
                    life: 3000,
                  });
                  return;
                }

                const { data } = await createOrUpdateOrder({
                  ...formOrderData,
                  shipping_cost: calculatedValue ?? "",
                });

                if (data?.success) {
                  toast.current.show({
                    severity: "info",
                    summary: t("Успешно сохранено!"),
                    life: 3000,
                  });
                  toggleSidebar(false);
                } else {
                  toast.current.show({
                    severity: "error",
                    summary: t("Ошибка при сохранении"),
                    life: 3000,
                  });
                }
                refetch();
              }}
            />
          </div>
        </div>
      </Sidebar>

      {/* -------- Sidebar: Reassign User -------- */}
      <Sidebar
        visible={userModal.visible}
        position="right"
        onHide={() => toggleNewUser(false)}
        style={{ minWidth: "50vw" }}
      >
        <p style={{ margin: 0 }}>
          {t("Перекидывать посылку на другого клиента")}:
        </p>
        <br />

        <Editor
          onBlur={(e, attr, value) =>
            setNewUser((s) => ({ ...s, [attr]: value }))
          }
          data={newUser}
          attr="new_user"
          placeholder={t("Поиск пользователя...")}
        />

        <br />
        <div>
          {Array.isArray(newUserData?.data) && newUserData.data.length
            ? `${newUserData.data[0].email} ${newUserData.data[0].client_code}`
            : t("Пользователь не найден")}
        </div>

        <br />

        {Array.isArray(newUserData?.data) && newUserData.data.length > 0 && (
          <MyButton
            label={t("save")}
            onClick={async () => {
              const { data } = await updateUser({
                guid: userModal.data.guid,
                new_user_guid: newUserData.data[0].guid,
              });

              if (data.success) {
                toast.current.show({
                  severity: "info",
                  summary: t("Успешно"),
                  life: 3000,
                });
                toggleNewUser(false);
              } else {
                toast.current.show({
                  severity: "error",
                  summary: t("Ошибка"),
                  life: 3000,
                });
              }
              refetch();
            }}
          />
        )}
      </Sidebar>

      {/* -------- Sidebar: PAYMENT -------- */}
      <Sidebar
        visible={paymentModal.visible}
        position="right"
        onHide={() => togglePayment(false)}
        style={{ width: "450px" }}
      >
        <h3 style={{ marginTop: 0 }}>{t("Оплата заказа")}</h3>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <b>{t("Клиент")}:</b> {paymentModal?.data?.client_fio}
            <br />
            <b>{t("Трек-код")}:</b> {paymentModal?.data?.tracking_number}
          </div>

          {/* SUMMA */}
          <Editor
            attr="summa"
            data={paymentModal.data}
            placeholder={t("Сумма")}
            onBlur={(e, attr, value) =>
              setPaymentModal((s) => ({
                ...s,
                data: { ...s.data, [attr]: value },
              }))
            }
          />

          {/* TYPE PAYMENT */}
          <Editor
            attr="type_payment"
            data={paymentModal.data}
            placeholder={t("Тип оплаты (нал / карта)")}
            onBlur={(e, attr, value) =>
              setPaymentModal((s) => ({
                ...s,
                data: { ...s.data, [attr]: value },
              }))
            }
          />

          <MyButton
            label={t("save")}
            onClick={async () => {
              if (!paymentModal.data?.summa) {
                toast.current.show({
                  severity: "error",
                  summary: t("Введите сумму оплаты"),
                  life: 3000,
                });
                return;
              }

              const { data, error } = await orderPay({
                order_guid: paymentModal.data.guid,
                summa: paymentModal.data.summa,
                type_payment: paymentModal.data.type_payment,
              });

              if (data?.success) {
                toast.current.show({
                  severity: "success",
                  summary: t("Оплата сохранена"),
                  life: 3000,
                });
                togglePayment(false);
                refetch();
              } else {
                toast.current.show({
                  severity: "error",
                  summary: t("Ошибка при оплате"),
                  detail: error?.data?.message,
                  life: 3000,
                });
              }
            }}
          />
        </div>
      </Sidebar>
    </div>
  );
}